import { useMemo, useRef, useState } from "react";

import type { AssetDataRecord, AssetDataSignal, AssetDataVal } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { dateTime } from "@/lib/format";
import { pretty } from "@/modules/asset_data/labels";

/** Format a decoded value for a table cell: integers as-is, floats to 2 dp,
 *  strings verbatim, null as a dim dash. */
export function fmt(v: AssetDataVal): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return String(v);
}

/** Columns = TS · SEQ · every catalog signal present (labelled + unit), THEN
 *  every other decoded field present (the long tail — gps_*, status_bits, batt_*,
 *  CAN slots, versions…), appended and prettified. This is the "dash-tool" view:
 *  one column per decoded variable, nothing hidden. */
export function useColumns(records: AssetDataRecord[], signals: AssetDataSignal[]) {
  const computed = useMemo(() => {
    const present = new Set<string>();
    for (const r of records) for (const k of Object.keys(r.values)) present.add(k);
    const catalog = signals.filter((s) => present.has(s.key));
    const known = new Set(signals.map((s) => s.key));
    const extras: AssetDataSignal[] = [...present]
      .filter((k) => !known.has(k))
      .sort()
      .map((k) => ({ key: k, label: pretty(k), unit: "" }));
    return [...catalog, ...extras];
  }, [records, signals]);

  // IDENTITY-STABLE columns: `records` gets a new array identity on every
  // append, but the column SET almost never changes — keep the previous array
  // whenever the keys match so DataTable's memoized rows keep skipping.
  const stable = useRef(computed);
  if (
    stable.current.length !== computed.length ||
    computed.some((c, i) => c.key !== stable.current[i]?.key)
  ) {
    stable.current = computed;
  }
  return stable.current;
}

/** The decoded telemetry table — a DataTable consumer. `newestFirst` flips row
 *  order for the live window (freshest on top); History leaves it
 *  oldest→newest for replay. Default column set = catalog key signals; the
 *  vendor long tail mounts behind the "All columns" toggle (cells are the
 *  mount budget — UI law 7). */
export function RecordTable({ records, signals, newestFirst = false, maxHeight = 420, pageSize = 100 }: {
  records: AssetDataRecord[]; signals: AssetDataSignal[]; newestFirst?: boolean; maxHeight?: number;
  /** cells are the mount budget: 100 rows × ~45 "All columns" ≈ 4.5k cells
   *  mounted — a 5000-row History window unpaged was ~225k (measured freeze) */
  pageSize?: number;
}) {
  const allCols = useColumns(records, signals);
  const [showAll, setShowAll] = useState(false);
  // Time sort: records arrive oldest→newest, so "sorting" is just a reverse —
  // the header toggles direction (▼ newest first / ▲ oldest first)
  const [desc, setDesc] = useState(newestFirst);
  const sigCols = useMemo(() => {
    if (showAll) return allCols;
    const known = new Set(signals.map((s) => s.key));
    const key = allCols.filter((c) => known.has(c.key));
    return key.length ? key : allCols;      // no catalog match → show everything
  }, [allCols, signals, showAll]);

  const cols = useMemo<DataCol<AssetDataRecord>[]>(() => [
    {
      key: "__ts",
      label: (
        <span style={{ cursor: "pointer", userSelect: "none" }} title="Sort by time"
          onClick={() => setDesc((d) => !d)}>
          Time {desc ? "▼" : "▲"}
        </span>
      ),
      width: 168, mono: true, render: (r) => dateTime(r.ts), title: (r) => new Date(r.ts).toLocaleString(),
    },
    { key: "__seq", label: "Seq", width: 58, mono: true, render: (r) => r.seq },
    ...sigCols.map((c): DataCol<AssetDataRecord> => ({
      key: c.key,
      label: <>{c.label}{c.unit && <span style={{ color: "var(--t-dim)", fontWeight: 400 }}> {c.unit}</span>}</>,
      width: 104,
      mono: true,
      render: (r) => fmt(r.values[c.key] ?? null),
      title: (r) => {
        const s = fmt(r.values[c.key] ?? null);
        return s.length > 12 ? s : undefined;
      },
    })),
  ], [sigCols, desc]);

  const rows = useMemo(
    () => (desc ? [...records].reverse() : records),
    [records, desc],
  );
  const hiddenCount = allCols.length - sigCols.length;

  return (
    <DataTable
      rows={rows}
      cols={cols}
      rowKey={(r) => r.cursor}
      stickyFirst
      maxHeight={maxHeight}
      fullscreenable
      pageSize={pageSize}
      empty={
        <div className="panel panel-pad cap">
          No decoded records yet for this device.
        </div>
      }
      toolbar={
        <button className="btn ghost" onClick={() => setShowAll((s) => !s)}
          title="Key signals = the catalog columns; All adds every decoded vendor field">
          {showAll ? `Key signals only` : `All columns${hiddenCount > 0 ? ` (+${hiddenCount})` : ""}`}
        </button>
      }
    />
  );
}
